import { v4 as uuid4 } from 'uuid';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type ToastType = 'download' | 'progress' | 'confirmation' | 'info' | 'alert' | 'error' | 'success';

export interface Toast {
    id: string;
    typeId?: string;
    title?: string;
    description?: string;
    progress?: number;
    failed?: boolean;
    type: ToastType;
    callback?: () => void;
    data?: { [key: string]: any };
}

export interface ToastStore {
    toasts: Toast[];
    addToast: (toast: Omit<Toast, 'id'>) => void;
    removeToast: (toastId: Toast['id']) => void;
    updateToasts: (toasts: Partial<Toast>[]) => void;
}

export const useToastStore = create<ToastStore>()(
    persist(
        (set, get) => ({
            toasts: [],
            addToast: (toast) => {
                const toasts = get().toasts;
                if (
                    toasts.some(
                        (t) =>
                            t.title === toast.title &&
                            t.description === toast.description &&
                            t.type === toast.type &&
                            t.typeId === toast.typeId
                    )
                ) {
                    return;
                }
                set({ ...get(), toasts: [...toasts, { ...toast, id: uuid4() }] });
            },
            removeToast: (toastId) => {
                set({ ...get(), toasts: get().toasts.filter((t) => t.id !== toastId) });
            },
            updateToasts: (toasts) => {
                set({
                    ...get(),
                    toasts: get().toasts.map((t) => {
                        const updatedToast = toasts.find((ut) => ut.id === t.id);
                        return updatedToast
                            ? {
                                  ...t,
                                  ...updatedToast
                              }
                            : t;
                    })
                });
            }
        }),
        {
            name: 'toast-storage', // name of the item in the storage (must be unique)
            storage: createJSONStorage(() => sessionStorage) // (optional) by default, 'localStorage' is used
        }
    )
);

const useToasts = useToastStore;

export default useToasts;
